import { faDatabase, faMemory, faCog, faClock, faCode } from '@fortawesome/free-solid-svg-icons';
import { getAllCacheData, getCacheStatus, cacheManager } from './cache-manager.js';
import { regexpHelpersRegistry } from '../wa-tools/regexpHelper.js';

/**
 * Générateur de source de données pour localStorage avec pattern (préfixe ou regex)
 */
export function createLocalStorageDataSource(pattern = 'bel_', name = 'LocalStorage') {
  // Déterminer si le pattern est une regex ou un string
  const isRegex = pattern instanceof RegExp;
  const patternDescription = isRegex ? pattern.toString() : `"${pattern}"`;
  
  // Fonction de test pour vérifier si une clé correspond au pattern
  const testKey = isRegex 
    ? (key) => pattern.test(key)
    : (key) => key.startsWith(pattern);
  
  return {
    name,
    description: `Données ${name} avec pattern ${patternDescription}`,
    icon: faDatabase,
    emptyMessage: `Aucune donnée correspondant au pattern ${patternDescription} n'a été trouvée dans le localStorage.`,
    
    loader: async () => {
      const data = {};
      
      // Parcourir toutes les clés du localStorage
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && testKey(key)) {
          try {
            const value = localStorage.getItem(key);
            // Essayer de parser en JSON, sinon garder la valeur string
            try {
              data[key] = JSON.parse(value);
            } catch (e) {
              data[key] = value;
            }
          } catch (err) {
            console.warn(`Erreur lors de la lecture de la clé ${key}:`, err);
            data[key] = null;
          }
        }
      }
      
      return data;
    },
    
    deleter: async (key) => {
      localStorage.removeItem(key);
    },
    
    clearAll: async () => {
      const keysToDelete = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && testKey(key)) {
          keysToDelete.push(key);
        }
      }
      keysToDelete.forEach(key => localStorage.removeItem(key));
    }
  };
}

/**
 * Générateur de source de données pour waItems
 */
export function createWaItemsDataSource(waItemContext) {
  return {
    name: 'waItems',
    description: 'Données des waItems en mémoire (Main + autres)',
    icon: faMemory,
    emptyMessage: 'Aucun waItem chargé. Utilisez la page Article Tools pour initialiser les waItems.',
    
    loader: async () => {
      const { mainWaItem, waItems } = waItemContext;
      const data = {};
      
      // Ajouter le waItem Main
      if (mainWaItem) {
        const mainKey = `waItem_${mainWaItem.role}`;
        data[mainKey] = {
          role: mainWaItem.role,
          isMain: mainWaItem.isMain,
          mainClass: mainWaItem.mainClass,
          id: mainWaItem.id,
          isLoaded: mainWaItem.isLoaded,
          belTagNames: mainWaItem.belTagNames,
          belTags: mainWaItem.belTags,
          otherTagNames: mainWaItem.otherTagNames,
          isProtected: mainWaItem.isProtected,
          axisaxisData: mainWaItem.axisaxisData,
          data: mainWaItem.data,
          metadata: {
            type: 'waItem',
            lastUpdate: new Date().toISOString()
          }
        };
      }
      
      // Ajouter les autres waItems
      waItems.forEach((item, index) => {
        const itemKey = `waItem_${item.role}_${index}`;
        data[itemKey] = {
          role: item.role,
          isMain: item.isMain,
          mainClass: item.mainClass,
          id: item.id,
          isLoaded: item.isLoaded,
          data: item.data,
          axisData: item.axisData,
          belTagNames: item.belTagNames,
          belTags: item.belTags,
          otherTagNames: item.otherTagNames,
          isProtected: item.isProtected,
          data: item.data,
          metadata: {
            type: 'waItem',
            lastUpdate: new Date().toISOString()
          }
        };
      });
      
      return data;
    },
    
    // Note: pas de deleter ni clearAll pour waItems car ils sont gérés par le contexte
    deleter: null,
    clearAll: null
  };
}

/**
 * Générateur de source de données pour sessionStorage avec pattern (préfixe ou regex)
 */
export function createSessionStorageDataSource(pattern = 'wa_', name = 'SessionStorage') {
  // Déterminer si le pattern est une regex ou un string
  const isRegex = pattern instanceof RegExp;
  const patternDescription = isRegex ? pattern.toString() : `"${pattern}"`;
  
  // Fonction de test pour vérifier si une clé correspond au pattern
  const testKey = isRegex 
    ? (key) => pattern.test(key)
    : (key) => key.startsWith(pattern);
  
  return {
    name,
    description: `Données ${name} avec pattern ${patternDescription}`,
    icon: faCog,
    emptyMessage: `Aucune donnée correspondant au pattern ${patternDescription} n'a été trouvée dans le sessionStorage.`,
    
    loader: async () => {
      const data = {};
      
      // Parcourir toutes les clés du sessionStorage
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (key && testKey(key)) {
          try {
            const value = sessionStorage.getItem(key);
            // Essayer de parser en JSON, sinon garder la valeur string
            try {
              data[key] = JSON.parse(value);
            } catch (e) {
              data[key] = value;
            }
          } catch (err) {
            console.warn(`Erreur lors de la lecture de la clé ${key}:`, err);
            data[key] = null;
          }
        }
      }
      
      return data;
    },
    
    deleter: async (key) => {
      sessionStorage.removeItem(key);
    },
    
    clearAll: async () => {
      const keysToDelete = [];
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (key && testKey(key)) {
          keysToDelete.push(key);
        }
      }
      keysToDelete.forEach(key => sessionStorage.removeItem(key));
    }
  };
}

/**
 * Générateur de source de données pour le cache mémoire
 */
export function createCacheDataSource() {
  return {
    name: 'memoryCache',
    description: 'Cache mémoire des requêtes API avec métadonnées',
    icon: faClock,
    emptyMessage: 'Aucune donnée en cache. Le cache se remplit automatiquement lors des appels API.',
    
    loader: async () => {
      const cacheData = getAllCacheData();
      const cacheStatus = getCacheStatus();
      
      // Ajouter des métadonnées globales
      const data = {
        _metadata: {
          totalEntries: Object.keys(cacheData).length,
          stats: cacheStatus.stats,
          loadingRequests: cacheStatus.loadingRequests,
          lastUpdate: new Date().toISOString()
        },
        ...cacheData
      };
      
      return data;
    },
    
    // Deleter pour supprimer une entrée spécifique du cache
    deleter: async (key) => {
      cacheManager.delete(key);
    },
    
    // ClearAll pour vider tout le cache
    clearAll: async () => {
      cacheManager.clear();
    }
  };
}

/**
 * Générateur de source de données pour les règles de conversion regex
 * Utilise le registre global des helpers pour récupérer automatiquement tous les helpers créés
 */
export function createRegexpRulesDataSource() {
  return {
    name: 'regexpRules',
    description: 'Règles de conversion avec expressions régulières depuis l\'API et localStorage',
    icon: faCode,
    emptyMessage: 'Aucune règle de conversion chargée. Les règles seront initialisées automatiquement.',
    
    loader: async () => {
      try {
        // Lazy load des parseurs BBCode (auto-cachés 6h)
        // Les lazy loaders sont appelés implicitement lors de la conversion
        // Aucune initialisation explicite nécessaire
        
        // Récupérer tous les helpers depuis le registre global
        const allHelpers = regexpHelpersRegistry.getAllHelpers();
        const helperNames = Object.keys(allHelpers);
        
        if (helperNames.length === 0) {
          return {
            _info: {
              message: 'Les helpers RegexpHelper seront créés à la première utilisation (lazy loading)',
              timestamp: new Date().toISOString()
            }
          };
        }
        
        // Préparer les métadonnées
        const directions = [];
        let totalRules = 0;
        let helpersInitialized = false;
        
        // Construire les données pour chaque helper
        const helpersData = {};
        
        helperNames.forEach(helperName => {
          const helper = allHelpers[helperName];
          directions.push(helperName);
          totalRules += helper.getRulesCount();
          
          // Vérifier si au moins un helper est initialisé
          if (helper.getInitialized?.()) {
            helpersInitialized = true;
          }
          
          // Déterminer une direction lisible pour les métadonnées
          let readableDirection = helperName;
          if (helperName === 'BBCodeToHtml') {
            readableDirection = 'BBCode → HTML';
          } else if (helperName === 'HtmlToBBCode') {
            readableDirection = 'HTML → BBCode';
          }
          
          // Créer une entrée pour ce helper
          helpersData[helperName.toLowerCase()] = {
            _metadata: {
              helper: helperName,
              direction: readableDirection,
              rulesCount: helper.getRulesCount(),
              initialized: helper.getInitialized()
            },
            rules: helper.getRules()
          };
        });
        
        // Organiser les données avec une clé par helper au niveau racine
        const data = {
          _metadata: {
            totalHelpers: helperNames.length,
            directions: directions,
            totalRules: totalRules,
            lastUpdate: new Date().toISOString(),
            helpersInitialized: helpersInitialized
          },
          ...helpersData
        };
        
        return data;
        
      } catch (error) {
        return {
          _error: {
            message: 'Erreur lors du chargement des règles de conversion avec regexpHelper',
            details: error.message,
            timestamp: new Date().toISOString()
          }
        };
      }
    },
    
    // Pas de deleter ni clearAll car les règles viennent de l'API
    deleter: null,
    clearAll: null
  };
}

/**
 * Générateur de source de données pour routing_config
 * Découpe le JSON en sections par clés de niveau 1 (text, vision, translation, wa)
 */
export function createRoutingConfigDataSource() {
  return {
    name: 'routing_config',
    description: 'Configuration de routage des appels API (IA et WorldAnvil)',
    icon: faDatabase,
    emptyMessage: 'La clé routing_config n\'a pas été trouvée dans le localStorage.',
    
    loader: async () => {
      const routingConfigRaw = localStorage.getItem('routing_config');
      
      if (!routingConfigRaw) {
        return {};
      }
      
      try {
        const routingConfig = JSON.parse(routingConfigRaw);
        const data = {};
        
        // Découper par clés de niveau 1 (text, vision, translation, wa, _metadata)
        Object.keys(routingConfig).forEach(topLevelKey => {
          data[`routing_config.${topLevelKey}`] = routingConfig[topLevelKey];
        });
        
        return data;
      } catch (error) {
        console.error('Erreur lors du parsing de routing_config:', error);
        return {
          _error: {
            message: 'Erreur lors du parsing de routing_config',
            details: error.message,
            rawValue: routingConfigRaw
          }
        };
      }
    },
    
    deleter: async (key) => {
      // Reconstituer la clé de niveau 1 depuis le format "routing_config.xxx"
      const topLevelKey = key.replace('routing_config.', '');
      
      const routingConfigRaw = localStorage.getItem('routing_config');
      if (!routingConfigRaw) return;
      
      try {
        const routingConfig = JSON.parse(routingConfigRaw);
        delete routingConfig[topLevelKey];
        localStorage.setItem('routing_config', JSON.stringify(routingConfig));
      } catch (error) {
        console.error('Erreur lors de la suppression de la clé routing_config:', error);
      }
    },
    
    clearAll: async () => {
      localStorage.removeItem('routing_config');
    }
  };
}

/**
 * Générateur de source de données personnalisée
 */
export function createCustomDataSource({ name, description, icon, loader, deleter, clearAll, emptyMessage }) {
  return {
    name,
    description,
    icon,
    emptyMessage,
    loader,
    deleter,
    clearAll
  };
}
